import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from './AuthProvider';

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: "select_account",
});

export default function Login() {
  const { user, approved } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [passwordResetError, setPasswordResetError] = useState('');
  const [passwordResetMessage, setPasswordResetMessage] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);

  // Redirect approved users to dashboard
  useEffect(() => {
    if (user && approved) {
      navigate('/dashboard');
    }
  }, [user, approved, navigate]);

  const handleLogin = async () => {
    setLoginError('');
    setPasswordResetError('');
    setPasswordResetMessage('');
    setIsLoggingIn(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Unable to sign in. Check your email and password and try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handlePasswordReset = async () => {
    const trimmedEmail = email.trim();

    setPasswordResetError('');
    setPasswordResetMessage('');
    setLoginError('');

    if (!trimmedEmail) {
      setPasswordResetError('Enter your email address first, then try again.');
      return;
    }

    setIsSendingPasswordReset(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Unable to send password reset email.');
      }

      setPasswordResetMessage(
        data.message || 'If an account exists for that email, check your inbox for reset instructions.'
      );
    } catch (error) {
      console.error('Password reset error:', error);
      setPasswordResetError('Unable to send password reset instructions right now. Please try again.');
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const handleGooglePopupLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      console.log('Popup login successful:', result.user.email);
      // Get the Firebase ID token

      //const token = await result.user.getIdToken(/* forceRefresh */ true);
   // console.log('🔑 Firebase ID Token:', token);
      //const tokenResult = await result.user.getIdTokenResult();
      //console.log('👤 Token claims:', tokenResult.claims);
    } catch (error) {
      console.error('Popup login error:', error);
    }
  };

  const handleLogout = () => {
    signOut(auth);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 shadow-2xl rounded-2xl overflow-hidden bg-white">
          {/* Left Column - Login Form */}
          <div className="px-8 py-12 sm:px-12 lg:px-16">
            {/* Logo and Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <img 
                  src="/distributely-logo-590x100.png"
                  alt="Distributely Logo"
                  className="h-12 w-auto"
                />
              </div>
              <h1 className="hidden text-4xl font-bold text-gray-900 mb-2">Distributely</h1>
              <p className="text-gray-600 text-lg">
                {user ? 'Account Status' : 'Sign in to your admin account'}
              </p>
            </div>

          {user ? (
            approved ? null : ( // Approved users are redirected, so this should never show
              <div className="text-center space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-medium">Hi {user.email}</p>
                  <p className="text-yellow-700 text-sm mt-1">Your account is awaiting approval.</p>
                </div>
                <button 
                  onClick={handleLogout}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Log Out
                </button>
              </div>
            )
          ) : (
            <div className="space-y-6">
              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
                  required
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
                  required
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={isSendingPasswordReset}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 transition duration-200 disabled:text-blue-300"
                  >
                    {isSendingPasswordReset ? 'Sending reset link...' : 'Forgot password?'}
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button 
                onClick={handleLogin}
                disabled={isLoggingIn}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isLoggingIn ? 'Signing In...' : 'Sign In'}
              </button>

              {loginError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {loginError}
                </div>
              ) : null}

              {passwordResetError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {passwordResetError}
                </div>
              ) : null}

              {passwordResetMessage ? (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {passwordResetMessage}
                </div>
              ) : null}

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <button 
                onClick={handleGooglePopupLogin}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 border border-gray-300 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center space-x-3"
              >
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google Logo"
                  className="w-5 h-5"
                />
                <span>Continue with Google</span>
              </button>

              {/* Sign Up Link */}
              <div className="text-center">
                <span className="text-gray-600 text-sm">Don't have an account? </span>
                <button 
                  onClick={() => {
                    const utmKeys = ['utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content'];
                    const params = new URLSearchParams();
                    utmKeys.forEach(k => { const v = searchParams.get(k); if (v) params.set(k, v); });
                    const qs = params.toString();
                    navigate(qs ? `/signup?${qs}` : '/signup');
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm transition duration-200"
                >
                  Create Account
                </button>
              </div>
            </div>
          )}
          </div>

          {/* Right Column - Descriptive Content with Gradient */}
          <div className="bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 px-8 py-12 sm:px-12 lg:px-16 flex items-center">
            <div className="w-full">
              <div className="text-center text-white">
                <div className="mb-8">
                  <h2 className="text-4xl font-bold mb-4 leading-tight">
                  Welcome to Distributely
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-300">
                    Complex HubSpot Routing, Simplified
                    </span>
                  </h2>
                  <p className="text-xl text-blue-100 leading-relaxed">
                  Automate complex routing across HubSpot with intelligent assignment rules for territories, capacity, compliance, and availability, without complex workflows. 
                  </p>
                </div>
                
                <div className="space-y-6">
                <div className="flex items-center text-left">
                    <div className="flex-shrink-0 w-3 h-3 bg-cyan-400 rounded-full mr-4"></div>
                    <p className="text-blue-100">Route every HubSpot object with advanced logic</p>
                  </div>
                  <div className="flex items-center text-left">
                    <div className="flex-shrink-0 w-3 h-3 bg-cyan-400 rounded-full mr-4"></div>
                    <p className="text-blue-100">Enforce licensing and compliance automatically</p>
                  </div>
                  <div className="flex items-center text-left">
                    <div className="flex-shrink-0 w-3 h-3 bg-cyan-400 rounded-full mr-4"></div>
                    <p className="text-blue-100">Balance workloads with capacity controls</p>
                  </div>
                  <div className="flex items-center text-left">
                    <div className="flex-shrink-0 w-3 h-3 bg-cyan-400 rounded-full mr-4"></div>
                    <p className="text-blue-100">Respect schedules, time zones, and availability</p>
                  </div>
                  <div className="flex items-center text-left">
                    <div className="flex-shrink-0 w-3 h-3 bg-cyan-400 rounded-full mr-4"></div>
                    <p className="text-blue-100">Test routing safely before deployment</p>
                  </div>
                </div>
                
                <div className="mt-12 pt-8 border-t border-blue-500/20">
                  <p className="text-blue-200 text-sm italic">
                    "Powering complex routing for modern revenue teams intelligently."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
